/**
 * serviceTypeBadge - Aparência do selo de Tipo de Serviço.
 *
 * O catálogo de serviços guarda uma cor sólida em service_types.color. No selo,
 * essa cor é suavizada em direção ao branco (tom pastel) e o texto é SEMPRE
 * branco — padrão visual aprovado pelo usuário (ver imagem de referência da
 * listagem do app do prestador: selos azul/vermelho pastel com texto branco).
 *
 * Reaproveita os helpers de src/lib/checkout/brandColor de propósito: o projeto
 * não deve ter uma segunda regra de cálculo de cor paralela.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-13 v1.2.0 - Texto do selo passa a ser SEMPRE branco (a regra
 *            de contraste de v1.1.0 trocava para texto escuro em cores claras e
 *            fugiu do visual aprovado). Fração pastel 0.30 mantida.
 *   RELEASE: 2026-10-13 v1.1.0 - Pastel passa a usar mixWithWhite (mistura real com
 *            branco): o clareio aditivo de v1.0.0 manteve a cor saturada e o selo
 *            continuou lendo como cor forte. Fração 0.30 suaviza sem apagar a identidade
 *            da cor cadastrada.
 *   RELEASE: 2026-10-13 v1.0.0 - Versão inicial: pastel discreto (15% em direção
 *            ao branco) aplicado no cartão de OS do app do prestador e na
 *            listagem de OS do desktop. Nome, altura do selo e fonte preservados.
 */
import type { CSSProperties } from 'react';
import { mixWithWhite } from '@/lib/checkout/brandColor';

/**
 * Fração de branco misturado à cor do catálogo.
 * 0.30 é o ponto em que o selo já lê como pastel, mas a cor cadastrada continua
 * reconhecível (pedido foi por uma tonalidade pastel "só um pouco").
 */
export const SERVICE_TYPE_PASTEL_FRACTION = 0.30;

/** Devolve a cor do catálogo suavizada em tom pastel. */
export function serviceTypePastelColor(color: string | null | undefined): string {
  return mixWithWhite(color, SERVICE_TYPE_PASTEL_FRACTION);
}

/**
 * Estilo do selo: fundo pastel + texto sempre branco (padrão visual aprovado).
 *
 * Sem cor cadastrada devolve `undefined`, deixando valer o fallback da classe
 * (`bg-primary`), exatamente como o cartão do app já fazia.
 */
export function serviceTypeBadgeStyle(color: string | null | undefined): CSSProperties | undefined {
  if (!color) return undefined;
  return { backgroundColor: serviceTypePastelColor(color), color: '#FFFFFF' };
}
